import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { isInternalPath } from './ExitIntentModal';
import { useCurrentPortalPaused } from '../../lib/maintenance';

export default function LaunchModal({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const paused = useCurrentPortalPaused();

  const dismiss = useCallback(() => {
    setOpen(false);
    document.body.style.overflow = '';
  }, []);

  const showModal = useCallback(() => {
    setOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    if (paused) {
      setOpen(false);
      document.body.style.overflow = '';
    }
  }, [paused]);

  // Intercept clicks on /download links
  useEffect(() => {
    const handler = (e) => {
      if (paused || isInternalPath(location.pathname)) return;
      // Don't intercept clicks inside the modal itself
      if (e.target.closest('[data-testid="launch-modal"]') || e.target.closest('[data-testid="launch-modal-backdrop"]')) return;

      const link = e.target.closest('a[href="/download"]');
      if (link) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        showModal();
        return false;
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [showModal, paused, location.pathname]);

  // ESC key to close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && open) {
        dismiss();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, dismiss]);

  // Redirect /download route
  useEffect(() => {
    if (paused) return;
    if (location.pathname === '/download') {
      window.history.replaceState(null, '', '/');
      showModal();
    }
  }, [location.pathname, showModal, paused]);

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <>
      {children}

      {open && !isInternalPath(location.pathname) && (
        <div
          data-testid="launch-modal-backdrop"
          onClick={dismiss}
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          style={{ backgroundColor: 'rgba(26, 43, 72, 0.75)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
        >
          <div
            data-testid="launch-modal"
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl p-8 text-center"
            style={{ backgroundColor: '#1A2B48', border: '1px solid #C5A059' }}
          >
            <button onClick={dismiss} data-testid="launch-modal-close" className="absolute top-3 right-3 text-white/40 hover:text-white/70 transition-colors">
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-center mb-6">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bmj-heartbeat" style={{ border: '1.5px solid #C5A059' }}>
                <svg width="24" height="24" viewBox="0 0 40 40" fill="none">
                  <rect x="4" y="8" width="24" height="24" rx="6" stroke="#C5A059" strokeWidth="2.5"/>
                  <path d="M16 16c0-2.2 1.8-4 4-4s4 1.8 4 4c0 4-4 7-4 7s-4-3-4-7z" fill="#C5A059"/>
                  <text x="26" y="28" fill="#C5A059" fontSize="18" fontWeight="700" fontFamily="Poppins, sans-serif">B</text>
                </svg>
              </div>
            </div>

            <h2
              className="text-xl font-bold mb-4 leading-snug"
              style={{ fontFamily: 'Poppins, sans-serif', color: '#C5A059' }}
            >
              A New Standard in Care is Arriving.
            </h2>

            <p
              className="text-sm leading-relaxed mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif', color: '#F0EDE8' }}
            >
              We are currently perfecting the BookMyJab experience to ensure your family receives the gold standard of at-home health concierge. Our mobile applications are undergoing final quality certification and will be available to our Gurgaon members within the week.
            </p>

            <button
              onClick={dismiss}
              data-testid="launch-modal-cta"
              className="inline-flex items-center justify-center rounded-full px-6 py-2.5 text-sm font-semibold transition-all hover:opacity-90"
              style={{ fontFamily: 'Montserrat, sans-serif', backgroundColor: '#C5A059', color: '#1A2B48' }}
            >
              Continue exploring our services
            </button>
          </div>
        </div>
      )}
    </>
  );
}
